import React from "react";
import { db } from "../../services/firebase";
import { collection, getDocs, query, where, doc, updateDoc } from "firebase/firestore";
import { getAllStudents } from "../../services/studentsService";
import { computeStudentFinance } from "../../utils/feeCalculator";
import { useLocation } from "react-router-dom";

function normCampus(v) {
  const x = String(v || "").toLowerCase().trim();
  if (x.includes("gapan")) return "gapan";
  if (x.includes("bulacan")) return "bulacan";
  return x;
}

export default function RecomputeStudentFees() {
  const location = useLocation();
  const [campus, setCampus] = React.useState("gapan");
  const [grade, setGrade] = React.useState("");
  const [apply, setApply] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [log, setLog] = React.useState("");
  const [count, setCount] = React.useState({ scanned: 0, updated: 0, skipped: 0 });

  const allGrades = React.useMemo(() => {
    try {
      const list = getAllStudents().map(s => s.gradeLevel).filter(Boolean);
      return ["", ...Array.from(new Set(list))];
    } catch { return [""]; }
  }, []);

  function append(t) { setLog(l => (l ? l + "\n" : "") + t); }

  async function run(doApply) {
    setBusy(true); setLog(""); setApply(doApply);
    let scanned = 0, updated = 0, skipped = 0;
    try {
      const qy = query(collection(db, "students"), where("campus", "==", normCampus(campus)));
      const snap = await getDocs(qy);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const rows = grade ? docs.filter(s => String(s.gradeLevel || "") === grade) : docs;
      scanned = rows.length;
      append(`Loaded ${rows.length} student(s) for campus ${campus}${grade ? " • " + grade : ""}.`);
      for (const s of rows) {
        const mode = s.paymentMode || "Annual";
        // Payments are read-only for recompute; outstanding is derived at runtime. Use empty payments here.
        const fin = computeStudentFinance({ ...s, paymentMode: mode }, [], "Annual");
        const schedule = (fin.schedule || []).map(e => ({
          label: e.label || e.period || "",
          misc: Number(e.misc || 0),
          tuition: Number(e.tuition || 0),
          total: Number(e.total || (Number(e.misc || 0) + Number(e.tuition || 0))),
        }));
        const totalDue = Number(fin.totalDue || 0);
        const prev = s.paymentSchedule && Array.isArray(s.paymentSchedule.schedule)
          ? s.paymentSchedule : null;
        const prevTotal = prev ? Number(prev.totalDue || 0) : null;
        const same = prev && Math.abs(Number(prevTotal) - totalDue) < 0.5;
        if (!doApply) {
          if (same) skipped++; else updated++;
          continue;
        }
        try {
          await updateDoc(doc(db, "students", s.id), {
            paymentSchedule: { schedule, totalDue },
            updatedAt: new Date().toISOString()
          });
          updated++;
        } catch (e) {
          skipped++;
          append(`Failed to update ${s.id}: ${e.message || String(e)}`);
        }
      }
    } catch (e) {
      append(`Error: ${e.message || String(e)}`);
    } finally {
      setCount({ scanned, updated, skipped });
      setBusy(false);
    }
  }

  React.useEffect(() => {
    try {
      const qs = new URLSearchParams(location.search || "");
      const c = qs.get("campus");
      const g = qs.get("grade");
      const auto = qs.get("apply");
      if (c) setCampus(c);
      if (g) setGrade(g);
      if (auto === "1" && !busy) {
        run(true);
      }
    } catch { /* no-op */ }
  }, []);

  return (
    <div className="container py-4">
      <h3 className="mb-3">Recompute Student Fees</h3>
      <div className="alert alert-info">
        Rebuilds each student's paymentSchedule from the latest tuition pricing. Existing payments are untouched; balances will be recomputed at runtime.
      </div>
      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <label className="form-label">Campus</label>
          <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)}>
            <option value="gapan">gapan</option>
            <option value="bulacan">bulacan</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">Grade (optional)</label>
          <select className="form-select" value={grade} onChange={e => setGrade(e.target.value)}>
            {allGrades.map(g => <option key={String(g)} value={String(g)}>{g || "All"}</option>)}
          </select>
        </div>
      </div>
      <div className="d-flex gap-2 mb-3">
        <button className="btn btn-outline-secondary" disabled={busy} onClick={() => run(false)}>Preview</button>
        <button className="btn btn-primary" disabled={busy} onClick={() => run(true)}>Apply Recompute</button>
      </div>
      <div className="mb-2">Scanned: {count.scanned} • Updated: {count.updated} • Skipped: {count.skipped}</div>
      {log && <div className="card"><div className="card-body"><pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{log}</pre></div></div>}
    </div>
  );
}
